/**
 * TicketDetail - one ticket beside the list: everything the sender wrote,
 * the page they were on, their files (TicketFiles: shown, downloaded, added),
 * the controls to work it (Tickets Edit), its history from the Activity log,
 * and "Copy for Claude".
 */
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { errorOf, getTicket, updateTicket } from './ticketsApi';
import TicketFiles from './TicketFiles';
import { ticketsChanged } from './useOpenTicketCount';
import { KIND_LABELS, PRIORITIES, PRIORITY_LABELS, STATUSES, STATUS_LABELS, copyForClaude, pageOf } from './ticketsRules';

const when = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');
const label = 'block text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-1';
const input = 'w-full border border-border-strong rounded px-2 py-1.5 text-[12px] bg-surface';

/** One audit row as a line: "status: Open -> Done". */
function historyLine(h) {
  if (/\/files$/.test(h.path || '')) return 'added files';
  if (/\/files\/\d+$/.test(h.path || '')) return `removed ${(h.detail && h.detail.snapshot && h.detail.snapshot.filename) || 'a file'}`;
  const changes = (h.detail && h.detail.changes) || {};
  const parts = Object.entries(changes).map(([k, v]) => {
    const show = (x) => (k === 'status' ? STATUS_LABELS[x] || x : k === 'priority' ? PRIORITY_LABELS[x] || x : (x ?? '-'));
    return `${k.replace(/_/g, ' ')}: ${show(v && v.from)} -> ${show(v && v.to)}`;
  });
  return parts.join('; ') || h.method;
}

export default function TicketDetail({ id, editable, onClose, onSaved }) {
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reads] = useState(latestReads);

  const load = async () => {
    const ticket = reads.start();
    const r = await getTicket(id);
    if (!reads.show(ticket)) return;
    if (!r.ok) return setErr(errorOf(r, 'Could not load the ticket.'));
    setErr(null);
    setT(r.body);
    setDraft({
      status: r.body.status,
      priority: r.body.priority,
      assigned_to: r.body.assigned_to || '',
      resolution_note: r.body.resolution_note || '',
    });
    return undefined;
  };
  useEffect(() => {
    setT(null);
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh('tickets', load, { match: (evt) => String(evt.id) === String(id) });

  if (err) return <div className="p-4 text-status-lost text-[12px]">{err}</div>;
  if (!t || !draft) return <div className="p-4 text-text-muted text-[12px]">Loading...</div>;

  const page = pageOf(t.page);
  const dirty = ['status', 'priority', 'assigned_to', 'resolution_note'].some((k) => (draft[k] || '') !== (t[k] || ''));

  const save = async () => {
    setBusy(true);
    const patch = {};
    for (const k of ['status', 'priority', 'assigned_to', 'resolution_note']) if ((draft[k] || '') !== (t[k] || '')) patch[k] = draft[k];
    const r = await updateTicket(t.id, patch);
    setBusy(false);
    if (!r.ok) return setErr(errorOf(r, 'Could not save.'));
    await load();
    ticketsChanged();
    if (onSaved) onSaved();
    return undefined;
  };

  const copy = async () => {
    const text = copyForClaude(t, window.location.origin);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-4 space-y-4 text-[12px]" aria-label={`Ticket ${t.number}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-text-muted font-semibold">
            {t.number} · {KIND_LABELS[t.kind] || t.kind}
          </div>
          <h2 className="text-[15px] font-bold text-text-dark break-words">{t.subject || '(no subject)'}</h2>
        </div>
        <Button size="sm" onClick={copy}>
          {copied ? 'Copied' : 'Copy for Claude'}
        </Button>
        <button type="button" aria-label="Close ticket" className="px-2 text-text-muted hover:text-text-dark" onClick={onClose}>
          ✕
        </button>
      </div>

      <dl className="grid grid-cols-[90px_1fr] gap-x-3 gap-y-1">
        <dt className="text-text-muted">From</dt>
        <dd>{t.from_name ? `${t.from_name} (${t.from_email})` : t.from_email || 'unknown'}</dd>
        <dt className="text-text-muted">Sent</dt>
        <dd>{when(t.created_at)}</dd>
        <dt className="text-text-muted">Page</dt>
        <dd className="break-all">
          {page.url ? (
            <a href={page.url} target="_blank" rel="noreferrer" className="text-gat-green font-semibold hover:underline">
              {page.label || page.url}
            </a>
          ) : (
            page.label || '-'
          )}
        </dd>
        <dt className="text-text-muted">Browser</dt>
        <dd className="break-words text-text-muted">{t.user_agent || '-'}</dd>
        {t.source_note && (
          <>
            <dt className="text-text-muted">Source</dt>
            <dd className="break-all">{t.source_note}</dd>
          </>
        )}
        {t.resolved_at && (
          <>
            <dt className="text-text-muted">Closed</dt>
            <dd>
              {when(t.resolved_at)} by {t.resolved_by || '?'}
            </dd>
          </>
        )}
      </dl>

      <div>
        <div className={label}>Details</div>
        <div className="whitespace-pre-wrap break-words bg-surface-2 rounded p-3 max-h-80 overflow-auto">{t.details || '(no details)'}</div>
      </div>

      <TicketFiles ticket={t} editable={editable} onChanged={onSaved} />

      <div className="grid grid-cols-2 gap-3">
        <label>
          <span className={label}>Status</span>
          <select
            className={input}
            value={draft.status}
            disabled={!editable}
            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            aria-label="Status"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={label}>Priority</span>
          <select
            className={input}
            value={draft.priority}
            disabled={!editable}
            onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
            aria-label="Priority"
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="col-span-2">
          <span className={label}>Assigned to</span>
          <input
            className={input}
            value={draft.assigned_to}
            disabled={!editable}
            onChange={(e) => setDraft({ ...draft, assigned_to: e.target.value })}
            aria-label="Assigned to"
          />
        </label>
        <label className="col-span-2">
          <span className={label}>Resolution note</span>
          <textarea
            className={`${input} min-h-[70px]`}
            value={draft.resolution_note}
            disabled={!editable}
            onChange={(e) => setDraft({ ...draft, resolution_note: e.target.value })}
            placeholder="What was done (the sender sees this in My tickets)"
            aria-label="Resolution note"
          />
        </label>
      </div>
      {editable && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={!dirty || busy}>
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}

      <div>
        <div className={label}>History</div>
        <ul className="space-y-1 text-text-body">
          <li>
            {when(t.created_at)} - {t.source === 'email_import' ? 'imported from email' : `sent by ${t.from_email || 'unknown'}`}
          </li>
          {(t.history || []).map((h, i) => (
            <li key={i}>
              {when(h.at)} - {h.actor || h.actor_email}: {historyLine(h)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
